/**
 * The fleet lives in the FLEET collection with PascalCase fields — the shape
 * OPTIMIZER-getFleets reads and the Vue optimizer writes. The app's own Vehicle
 * type is the camelCase shape getFleets *returns*, which is NOT what's stored, so
 * every write has to be mapped back through here.
 *
 * Two traps this module exists to avoid:
 *   - Firestore collection ids are case-sensitive: `fleet` is a different (empty,
 *     unread) collection from `FLEET`.
 *   - StartLocation/EndLocation store a distribution zone's DOC ID, not its name.
 */
export const FLEET_COLLECTION = 'FLEET'

/** Piece-count capacity. Not exposed in the form; every vehicle gets this, matching
 *  the Vue optimizer's default and what stored FLEET docs already carry. */
export const DEFAULT_CAPACITY = 10000

/** app field → FLEET field. A field absent here has no FLEET column and is not
 *  persisted — `returnToStart` is the notable one: getFleets returns it, but the
 *  backend derives it and no stored doc has it. */
const FIELD_MAP: Record<string, string> = {
  name: 'Name',
  region: 'Region',
  vehicleType: 'VehicleType',
  isActive: 'Active',
  startLocation: 'StartLocation',
  endLocation: 'EndLocation',
  shiftStart: 'ShiftStart',
  shiftEnd: 'ShiftEnd',
  capacity: 'Capacity',
  weightCapacity: 'WeightCapacity',
  volumeCapacity: 'VolumeCapacity',
}

export interface Zone { id?: string; name?: string; region?: string }

const isLocationField = (field: string) => field === 'startLocation' || field === 'endLocation'

/** A depot is stored as the zone's doc id; the form works in zone names. */
export const zoneIdByName = (zones: Zone[] | undefined, name: string): string | undefined =>
  (zones ?? []).find((z) => z.name === name)?.id

const numberOr = (value: any, fallback: number): number => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

/**
 * One app-level field edit as a FLEET patch, or null when the field has no FLEET
 * column or a depot name can't be resolved to a zone id — null means "don't write",
 * which is safer than writing a name where an id belongs.
 */
export const toFleetUpdate = (
  field: string,
  value: any,
  zones?: Zone[],
): Record<string, any> | null => {
  const mapped = FIELD_MAP[field]
  if (!mapped) return null
  if (isLocationField(field)) {
    const id = zoneIdByName(zones, value)
    return id === undefined ? null : { [mapped]: id }
  }
  return { [mapped]: value }
}

/** The full FLEET document for a new vehicle. Capacities arrive already metric.
 *  `Id` duplicates the doc id — stored docs carry it and the backend reads it. */
export const toFleetDoc = (vehicle: any, id: string, zones?: Zone[]): Record<string, any> => ({
  Id: id,
  Name: vehicle.name ?? '',
  Region: vehicle.region ?? '',
  VehicleType: vehicle.vehicleType ?? '',
  Active: vehicle.isActive !== false,
  IsVehicleLocked: false,
  StartLocation: zoneIdByName(zones, vehicle.startLocation?.name ?? vehicle.startLocation) ?? '',
  EndLocation: zoneIdByName(zones, vehicle.endLocation?.name ?? vehicle.endLocation) ?? '',
  ShiftStart: vehicle.shiftStart ?? '',
  ShiftEnd: vehicle.shiftEnd ?? '',
  Capacity: numberOr(vehicle.capacity, DEFAULT_CAPACITY) || DEFAULT_CAPACITY,
  WeightCapacity: numberOr(vehicle.weightCapacity, 0),
  VolumeCapacity: numberOr(vehicle.volumeCapacity, 0),
})
